/**
 * Payroll vs Register analysis engine.
 * Spending entity = Employee Register department (not a separate payroll column).
 */

export function normId(v) {
  if (v == null || v === "") return "";
  return String(v).trim().replace(/\s+/g, "").toUpperCase();
}

export function normKeyHeader(h) {
  return String(h || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

export function pickColumn(headers, patterns) {
  const list = (headers || []).map((h) => ({ raw: h, key: normKeyHeader(h) }));
  for (const p of patterns) {
    const pl = p.toLowerCase();
    const exact = list.find((x) => x.key === pl);
    if (exact) return exact.raw;
  }
  for (const p of patterns) {
    const pl = p.toLowerCase();
    const sub = list.find((x) => x.key.includes(pl));
    if (sub) return sub.raw;
  }
  return null;
}

export function parseMoney(v) {
  if (v == null || v === "") return 0;
  if (typeof v === "number" && !Number.isNaN(v)) return v;
  const s = String(v).replace(/,/g, "").replace(/[^\d.-]/g, "");
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
}

/** Detect earning (8xx) and deduction (9xx) code columns from ledger headers. */
export function detectCodeColumns(headers) {
  const earnings = [];
  const deductions = [];
  for (const h of headers || []) {
    const raw = String(h || "").trim();
    if (!raw) continue;
    const m = raw.match(/^(\d{3})\s+(.+)$/);
    if (!m) continue;
    const code = m[1];
    const label = m[2].trim();
    const num = parseInt(code, 10);
    const entry = { code, label, header_name: raw };
    if (num >= 800 && num <= 899) earnings.push(entry);
    else if (num >= 900 && num <= 999) deductions.push(entry);
  }
  earnings.sort((a, b) => a.code.localeCompare(b.code));
  deductions.sort((a, b) => a.code.localeCompare(b.code));
  return { earnings, deductions };
}

export function isVoluntaryPensionText(text) {
  const t = String(text || "").toLowerCase();
  if (!t) return false;
  return (
    t.includes("voluntary") ||
    t.includes("avc") ||
    t.includes("additional voluntary") ||
    t.includes("pension voluntary") ||
    /\b957\b/.test(t)
  );
}

export function maskSensitive(value, mask = true) {
  if (!mask || value == null || value === "") return value;
  const s = String(value).trim();
  if (s.length <= 4) return "****";
  return s.slice(0, 2) + "*".repeat(Math.min(8, s.length - 4)) + s.slice(-2);
}

const PAYROLL_FIELD_PATTERNS = {
  payrollNum: ["payrollnum", "payroll num", "pf no", "p/f no", "personal number"],
  idNum: ["idnum", "id num", "national id", "nationalid", "id no"],
  taxPin: ["taxpin", "tax pin", "kra pin", "pin"],
  names: ["names", "employee name", "staff name", "full name", "name"],
  gender: ["gender", "sex"],
  dateHired: ["datehired", "date hired", "hire date", "employment date"],
  designation: ["designation", "position", "job title"],
  jobGroup: ["jobgroup", "job group", "grade", "salary scale"],
  voteCode: ["votecode", "vote code"],
  voteName: ["votename", "vote name"],
  subVoteName: ["subvotename", "sub vote"],
  headName: ["headname", "head name"],
  subHeadName: ["subheadname", "sub head"],
  wCountyName: ["wcountyname", "work county", "county"],
  grossPay: ["grosspay", "gross pay", "gross salary"],
  totalDeductions: ["totaldeductions", "total deductions", "totaldeductions_athird"],
  netPay: ["netpay", "net pay", "net salary"]
};

const PENSION_FIELD_PATTERNS = {
  paydate: ["paydate", "pay date", "payment date"],
  voteCode: ["votecode", "vote code"],
  payrollNum: ["payrollnum", "payroll num", "pf no"],
  idNum: ["idnum", "id num", "national id"],
  edCode: ["edcode", "ed code", "deduction code"],
  amount: ["amount", "deduction", "contribution"],
  balance: ["balance"],
  name: ["name", "employee name"],
  remarks: ["remarks", "remark", "notes"]
};

const GRATUITY_FIELD_PATTERNS = {
  payrollNum: ["payrollnum", "payroll num", "pf no", "emp no"],
  idNum: ["idnum", "id num", "national id"],
  name: ["name", "employee name"],
  amount: ["amount", "gratuity", "gratuity amount", "total"],
  voteCode: ["votecode", "vote code"],
  department: ["department", "dept", "directorate"]
};

export function autoMapTemplateFields(headers, templateType) {
  const patterns =
    templateType === "pension"
      ? PENSION_FIELD_PATTERNS
      : templateType === "gratuity"
        ? GRATUITY_FIELD_PATTERNS
        : PAYROLL_FIELD_PATTERNS;
  const out = {};
  for (const [key, pats] of Object.entries(patterns)) {
    out[key] = pickColumn(headers, pats) || "";
  }
  return out;
}

export function buildRegisterIndex(registerList) {
  const byId = new Map();
  for (const e of registerList) {
    const a = normId(e.empNo);
    const b = normId(e.nationalId);
    if (a) byId.set(a, e);
    if (b) byId.set(b, e);
  }
  return byId;
}

export function findDuplicatePayrollIds(csvRows, idCol) {
  const seen = new Map();
  const dups = [];
  if (!idCol) return dups;
  for (let i = 0; i < csvRows.length; i++) {
    const nid = normId(csvRows[i][idCol]);
    if (!nid) continue;
    if (seen.has(nid)) {
      dups.push({ staffId: nid, rowIndexes: [seen.get(nid), i] });
    } else {
      seen.set(nid, i);
    }
  }
  return dups;
}

function strEq(a, b) {
  return String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase();
}

function isInactiveStatus(status) {
  const s = String(status || "").toLowerCase();
  return ["inactive", "terminated", "retired", "separated", "resigned", "deceased", "dismissed"].some((x) =>
    s.includes(x)
  );
}

/**
 * Core register vs payroll reconciliation.
 * Spending entity for matched employees = register departmentName.
 */
export function runRegisterPayrollAnalysis({
  csvRows,
  headers,
  registerList,
  columnMaps = {},
  nitaPerEmployee = 0
}) {
  const mapId = columnMaps.mapId || pickColumn(headers, ["payrollnum", "idnum", "pf", "emp", "staff", "national"]);
  const mapDept = columnMaps.mapDept || pickColumn(headers, ["department", "dept"]);
  const mapPay =
    columnMaps.mapPay ||
    pickColumn(headers, ["grosspay", "gross"]) ||
    pickColumn(headers, ["netpay", "net"]) ||
    pickColumn(headers, ["basic"]);
  const mapName = columnMaps.mapName || pickColumn(headers, ["names", "name"]);
  const mapDesignation = columnMaps.mapDesignation || pickColumn(headers, ["designation", "position"]);
  const mapJobGroup = columnMaps.mapJobGroup || pickColumn(headers, ["jobgroup", "job group", "grade"]);

  const grossCol = pickColumn(headers, ["grosspay", "gross pay"]);
  const netCol = pickColumn(headers, ["netpay", "net pay"]);
  const dedCol = pickColumn(headers, ["totaldeductions", "total deductions"]);
  const pension902Col =
    pickColumn(headers, ["902 pension", "902 pension (auto)", "pension (auto)"]) ||
    (headers || []).find((h) => /^902\b/i.test(String(h))) ||
    null;

  const { earnings, deductions } = detectCodeColumns(headers);
  const byId = buildRegisterIndex(registerList);
  const matchedIds = new Set();
  const duplicateRows = findDuplicatePayrollIds(csvRows, mapId);

  let totalGross = 0;
  let totalNet = 0;
  let totalDeductions = 0;
  let totalPayrollPension902 = 0;

  const wageMap = new Map();
  const spendingMap = new Map();
  const csvOnly = [];
  const matchedDetail = [];
  const deptVariances = [];
  const designationVariances = [];
  const jobGroupVariances = [];
  const inactivePaid = [];

  for (const row of csvRows) {
    const rawId = mapId ? row[mapId] : null;
    const nid = normId(rawId);
    const pay = mapPay ? parseMoney(row[mapPay]) : 0;
    const gross = grossCol ? parseMoney(row[grossCol]) : pay;
    const net = netCol ? parseMoney(row[netCol]) : 0;
    const ded = dedCol ? parseMoney(row[dedCol]) : 0;
    const pension902 = pension902Col ? parseMoney(row[pension902Col]) : 0;

    totalGross += gross;
    totalNet += net || pay;
    totalDeductions += ded;
    totalPayrollPension902 += pension902;

    const csvDept = (mapDept && row[mapDept] != null ? String(row[mapDept]).trim() : "") || "";
    const nm = mapName ? String(row[mapName] || "").trim() : "";
    const csvDesig = mapDesignation ? String(row[mapDesignation] || "").trim() : "";
    const csvJg = mapJobGroup ? String(row[mapJobGroup] || "").trim() : "";

    const emp = nid ? byId.get(nid) : null;
    const spendingEntity = emp
      ? String(emp.departmentName || emp.departmentID || "(No department)").trim() || "(No department)"
      : csvDept || "(Unknown — not in register)";

    const wageKey = csvDept || spendingEntity;
    const prevW = wageMap.get(wageKey) || { total: 0, count: 0, gross: 0, net: 0 };
    prevW.total += pay;
    prevW.gross += gross;
    prevW.net += net || pay;
    prevW.count += 1;
    wageMap.set(wageKey, prevW);

    const sp = spendingMap.get(spendingEntity) || {
      entity: spendingEntity,
      staffCount: 0,
      gross: 0,
      net: 0,
      deductions: 0,
      pension902: 0,
      nita: 0
    };
    sp.staffCount += 1;
    sp.gross += gross;
    sp.net += net || pay;
    sp.deductions += ded;
    sp.pension902 += pension902;
    spendingMap.set(spendingEntity, sp);

    if (emp) {
      const e1 = normId(emp.empNo);
      const e2 = normId(emp.nationalId);
      if (e1) matchedIds.add(e1);
      if (e2) matchedIds.add(e2);
      const regDept = emp.departmentName || "";
      const deptMismatch = csvDept && regDept && !strEq(regDept, csvDept);
      const desigMismatch = csvDesig && emp.designation && !strEq(emp.designation, csvDesig);
      const jgMismatch = csvJg && emp.jobGroup && !strEq(emp.jobGroup, csvJg);
      const inactiveIssue = isInactiveStatus(emp.status);

      const detail = {
        empNo: emp.empNo,
        nationalId: emp.nationalId,
        regName: [emp.firstName, emp.middleName, emp.lastName].filter(Boolean).join(" ").trim(),
        regDept,
        spendingEntity,
        csvDept,
        deptMismatch,
        regDesignation: emp.designation || "",
        csvDesignation: csvDesig,
        designationMismatch: desigMismatch,
        regJobGroup: emp.jobGroup || "",
        csvJobGroup: csvJg,
        jobGroupMismatch: jgMismatch,
        status: emp.status,
        inactivePaid: inactiveIssue,
        pay,
        gross,
        net: net || pay,
        pension902
      };
      matchedDetail.push(detail);
      if (deptMismatch) deptVariances.push(detail);
      if (desigMismatch) designationVariances.push(detail);
      if (jgMismatch) jobGroupVariances.push(detail);
      if (inactiveIssue) inactivePaid.push(detail);
    } else {
      csvOnly.push({ staffId: rawId, name: nm, dept: csvDept, spendingEntity, pay, gross });
    }
  }

  const registerOnly = registerList.filter((e) => {
    const k1 = normId(e.empNo);
    const k2 = normId(e.nationalId);
    return !((k1 && matchedIds.has(k1)) || (k2 && matchedIds.has(k2)));
  });

  const employeeCount = matchedDetail.length;
  const nitaExpected = employeeCount * (Number(nitaPerEmployee) || 0);
  const nitaCol = pickColumn(headers, ["nita"]);
  let nitaActual = 0;
  if (nitaCol) {
    for (const row of csvRows) nitaActual += parseMoney(row[nitaCol]);
  } else {
    nitaActual = nitaExpected;
  }

  for (const sp of spendingMap.values()) {
    sp.nita = sp.staffCount * (Number(nitaPerEmployee) || 0);
    sp.totalCost = sp.gross + sp.pension902 + sp.nita;
  }

  const spendingSummaryRows = Array.from(spendingMap.values()).sort((a, b) =>
    String(a.entity).localeCompare(String(b.entity))
  );
  spendingSummaryRows.forEach((r, i) => {
    r.seq = i + 1;
    r.key = String(r.entity).toLowerCase();
  });

  const wageBillRows = Array.from(wageMap.entries())
    .map(([dept, v]) => ({ dept, total: v.total, gross: v.gross, net: v.net, count: v.count }))
    .sort((a, b) => b.total - a.total);

  const financialSummary = {
    grossPay: totalGross,
    totalDeductions,
    netPay: totalNet,
    payrollPension902: totalPayrollPension902,
    nitaExpected,
    nitaActual,
    nitaVariance: nitaActual - nitaExpected,
    employeeCount
  };

  const summary = {
    csvRowCount: csvRows.length,
    registerCount: registerList.length,
    matchedCount: matchedDetail.length,
    csvOnlyCount: csvOnly.length,
    registerOnlyCount: registerOnly.length,
    duplicateCount: duplicateRows.length,
    deptVarianceCount: deptVariances.length,
    designationVarianceCount: designationVariances.length,
    jobGroupVarianceCount: jobGroupVariances.length,
    inactivePaidCount: inactivePaid.length,
    totalCsvPay: totalNet || totalGross,
    ...financialSummary
  };

  const codesForCatalog = [
    ...earnings.map((e) => ({ code: e.code, code_type: "earning", label: e.label, header_name: e.header_name })),
    ...deductions.map((d) => ({ code: d.code, code_type: "deduction", label: d.label, header_name: d.header_name }))
  ];

  return {
    summary,
    financialSummary,
    wageBillRows,
    registerOnlyRows: registerOnly,
    csvOnlyRows: csvOnly,
    matchedDetailRows: matchedDetail,
    spendingSummaryRows,
    duplicateRows,
    deptVariances,
    designationVariances,
    jobGroupVariances,
    inactivePaidRows: inactivePaid,
    earnings,
    deductions,
    pension902Col,
    codesForCatalog,
    columnMapsResolved: { mapId, mapDept, mapPay, mapName, mapDesignation, mapJobGroup }
  };
}

/** Parse pension rows from up to 4 files; reconcile mandatory EDCode 902 only. */
export function reconcilePension({ payrollMatchedRows, pensionFileRowsList, pensionMapsList }) {
  const payrollByKey = new Map();
  for (const r of payrollMatchedRows || []) {
    const k1 = normId(r.empNo);
    const k2 = normId(r.nationalId);
    const amt = Number(r.pension902) || 0;
    if (k1) payrollByKey.set(k1, { ...r, pensionPayroll: amt });
    if (k2) payrollByKey.set(k2, { ...r, pensionPayroll: amt });
  }

  const pensionRecords = [];
  const filesArray = Array.isArray(pensionFileRowsList)
    ? pensionFileRowsList
    : typeof pensionFileRowsList === "object" && pensionFileRowsList !== null
      ? Object.values(pensionFileRowsList)
      : [];

  for (let fi = 0; fi < filesArray.length; fi++) {
    const rows = filesArray[fi] || [];
    const maps = (pensionMapsList && pensionMapsList[fi]) || autoMapTemplateFields(rows[0] ? Object.keys(rows[0]) : [], "pension");
    const edCol = maps.edCode || pickColumn(Object.keys(rows[0] || {}), ["edcode", "ed code"]);
    const amtCol = maps.amount || pickColumn(Object.keys(rows[0] || {}), ["amount"]);
    const idCol = maps.payrollNum || maps.idNum || pickColumn(Object.keys(rows[0] || {}), ["payrollnum", "idnum"]);
    const nameCol = maps.name;
    const remarksCol = maps.remarks;

    for (const row of rows) {
      const edRaw = edCol ? String(row[edCol] ?? "").trim() : "";
      const edCode = edRaw.replace(/\D/g, "").slice(0, 3) || edRaw;
      if (edCode !== "902" && !/^902\b/.test(edRaw)) continue;

      const remarks = remarksCol ? String(row[remarksCol] || "") : "";
      const nameVal = nameCol ? String(row[nameCol] || "") : "";
      if (isVoluntaryPensionText(remarks) || isVoluntaryPensionText(nameVal) || isVoluntaryPensionText(edRaw)) continue;

      const rawId = idCol ? row[idCol] : "";
      const key = normId(rawId);
      if (!key) continue;
      const amt = amtCol ? parseMoney(row[amtCol]) : 0;
      pensionRecords.push({ key, rawId, amount: amt, fileIndex: fi + 1, name: nameVal });
    }
  }

  const pensionByKey = new Map();
  for (const p of pensionRecords) {
    const prev = pensionByKey.get(p.key) || { ...p, amount: 0 };
    prev.amount += p.amount;
    pensionByKey.set(p.key, prev);
  }

  const matched = [];
  const missingInPension = [];
  const missingInPayroll = [];
  const variances = [];
  let payrollTotal = 0;
  let pensionTotal = 0;

  const allKeys = new Set([...payrollByKey.keys(), ...pensionByKey.keys()]);
  for (const key of allKeys) {
    const pr = payrollByKey.get(key);
    const pe = pensionByKey.get(key);
    const pAmt = pr ? Number(pr.pensionPayroll) || 0 : 0;
    const eAmt = pe ? Number(pe.amount) || 0 : 0;
    if (pr) payrollTotal += pAmt;
    if (pe) pensionTotal += eAmt;

    if (pr && pe) {
      const variance = pAmt - eAmt;
      const row = {
        key,
        empNo: pr.empNo,
        nationalId: pr.nationalId,
        name: pr.regName,
        department: pr.spendingEntity || pr.regDept,
        payrollPension: pAmt,
        pensionFile: eAmt,
        variance,
        status: Math.abs(variance) < 0.01 ? "matched" : variance > 0 ? "overpayment" : "underpayment"
      };
      matched.push(row);
      if (Math.abs(variance) >= 0.01) variances.push(row);
    } else if (pr && !pe) {
      missingInPension.push({
        key,
        empNo: pr.empNo,
        nationalId: pr.nationalId,
        name: pr.regName,
        department: pr.spendingEntity,
        payrollPension: pAmt
      });
    } else if (!pr && pe) {
      missingInPayroll.push({
        key,
        rawId: pe.rawId,
        name: pe.name,
        pensionFile: eAmt
      });
    }
  }

  return {
    matched,
    missingInPension,
    missingInPayroll,
    variances,
    totals: {
      payrollPension902: payrollTotal,
      pensionFiles902: pensionTotal,
      variance: payrollTotal - pensionTotal,
      matchedCount: matched.length,
      varianceCount: variances.length
    },
    pensionRecordCount: pensionRecords.length
  };
}

/** Gratuity reconciliation vs payroll/register. */
export function reconcileGratuity({ gratuityRows, headers, registerList, payrollMatchedRows }) {
  const maps = autoMapTemplateFields(headers, "gratuity");
  const idCol = maps.payrollNum || maps.idNum;
  const amtCol = maps.amount;
  const byId = buildRegisterIndex(registerList);
  const payrollByKey = new Map();
  for (const r of payrollMatchedRows || []) {
    const k1 = normId(r.empNo);
    const k2 = normId(r.nationalId);
    if (k1) payrollByKey.set(k1, r);
    if (k2) payrollByKey.set(k2, r);
  }

  const uploaded = [];
  let uploadedTotal = 0;
  for (const row of gratuityRows || []) {
    const rawId = idCol ? row[idCol] : null;
    const key = normId(rawId);
    const amt = amtCol ? parseMoney(row[amtCol]) : 0;
    uploadedTotal += amt;
    const emp = key ? byId.get(key) : null;
    uploaded.push({
      key,
      rawId,
      amount: amt,
      name: maps.name ? row[maps.name] : "",
      department: emp ? emp.departmentName : maps.department ? row[maps.department] : "",
      inRegister: !!emp,
      inPayroll: key ? payrollByKey.has(key) : false
    });
  }

  const missingInGratuity = (payrollMatchedRows || [])
    .filter((r) => {
      const k1 = normId(r.empNo);
      const k2 = normId(r.nationalId);
      return ![k1, k2].some((k) => k && uploaded.some((u) => u.key === k));
    })
    .map((r) => ({
      empNo: r.empNo,
      nationalId: r.nationalId,
      name: r.regName,
      department: r.spendingEntity
    }));

  return {
    uploadedRows: uploaded,
    uploadedTotal,
    missingInGratuity,
    missingCount: missingInGratuity.length
  };
}

export function buildExecutiveSummary({
  financialSummary,
  pensionRecon,
  gratuityRecon,
  spendingSummaryRows,
  variancesTopN = 10
}) {
  const fs = financialSummary || {};
  const pr = pensionRecon?.totals || {};
  const gr = gratuityRecon || {};
  const allVariances = [...(pensionRecon?.variances || [])];
  allVariances.sort((a, b) => Math.abs(b.variance) - Math.abs(a.variance));
  const topVariances = allVariances.slice(0, variancesTopN);

  const spendingEntitySummary = (spendingSummaryRows || []).map((r) => ({
    department: r.entity,
    employeeCount: r.staffCount,
    payrollCost: r.gross,
    pensionCost: r.pension902,
    nitaCost: r.nita,
    totalCost: r.totalCost
  }));

  return {
    totalPayrollCost: fs.grossPay || 0,
    totalPensionCost: pr.pensionFiles902 || fs.payrollPension902 || 0,
    totalGratuityCost: gr.uploadedTotal || 0,
    totalNitaCost: fs.nitaExpected || 0,
    totalVariance: (pr.variance || 0) + (fs.nitaVariance || 0),
    employeeCount: fs.employeeCount || 0,
    topVariances,
    spendingEntitySummary
  };
}
